import {Moment} from 'moment'

import type {DatetimeValue} from '../api'

/**
 * function to interpolate missing values linearly from nearest values
 * if value is null it will be interpolated
 * if first value is null it it will be replaced with 0
 */
export const interpolateDatetimeValues = <T extends Moment | string>(
  datetimeValues: DatetimeValue<T, null | number>[]
): DatetimeValue<T, number>[] => {
  const interpolatedArray: DatetimeValue<T, number>[] = []

  for (let i = 0; i < datetimeValues.length; i++) {
    const current = datetimeValues[i]
    if (current.value !== null) {
      interpolatedArray.push({datetime: current.datetime, value: current.value})
    } else {
      let prevNonNullIndex = i - 1
      let nextNonNullIndex = i + 1

      // Find the nearest non-null values
      while (!datetimeValues[prevNonNullIndex]?.value && prevNonNullIndex > 0) {
        prevNonNullIndex--
      }

      while (!datetimeValues[nextNonNullIndex]?.value && nextNonNullIndex < datetimeValues.length) {
        nextNonNullIndex++
      }

      const diffBetweenIndexes = nextNonNullIndex - prevNonNullIndex

      // Linear interpolation between the nearest non-null values
      const prevNonNullValue = datetimeValues[prevNonNullIndex]?.value
      const nextNonNullValue = datetimeValues[nextNonNullIndex]?.value
      // if series start or ends with null
      if (!prevNonNullValue || !nextNonNullValue) {
        interpolatedArray.push({datetime: current.datetime, value: 0})
      } else {
        const valuePerEntry = (nextNonNullValue - prevNonNullValue) / diffBetweenIndexes
        const value = prevNonNullValue + valuePerEntry * (i - prevNonNullIndex)
        interpolatedArray.push({datetime: current.datetime, value})
      }
    }
  }
  return interpolatedArray
}
